/**
 * AiImproveDialog
 *
 * A focused dialog for the "Improve with AI" context menu action.
 *
 * Flow:
 *  1. Dialog opens → user types what they want improved and clicks "Improve".
 *  2. AI streams the improved code snippet back.
 *  3. A diff view shows the original vs. improved code.
 *  4. "Accept" replaces the selection in the editor; "Discard" closes without changes.
 *  5. After the initial response the user can ask follow-up questions in a chat thread.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Wand2,
  Settings,
  Send,
  Square,
  Bot,
  User,
  Check,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { OpenRouterModel } from '@/types/ai';
import { useAiSettings } from '@/hooks/useAiSettings';
import { useAiImprove } from '@/hooks/useAiImprove';
import { createAiClient, fetchModels } from '@/services/ai';
import { extractImprovedCode } from '@/services/aiPrompts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AiTokenDisplay } from './AiTokenDisplay';
import { AiSettingsDialog } from './AiSettingsDialog';
import { ModelSelector } from './ModelSelector';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AiImproveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The code the user selected in the editor */
  selectedCode: string;
  /** The full editor content — sent as background context */
  contextCode: string;
  /** Called with the improved code when the user clicks Accept */
  onApply: (improvedCode: string) => void;
  /** Pre-fills the instruction textarea (editable by the user before firing) */
  initialInstruction?: string;
  /** Replaces the default buildImproveSystemPrompt — forwarded to the hook */
  systemPromptOverride?: string;
  /** Override the dialog title (defaults to "Improve with AI") */
  title?: string;
}

export function AiImproveDialog({
  open,
  onOpenChange,
  selectedCode,
  contextCode,
  onApply,
  initialInstruction,
  systemPromptOverride,
  title = 'Improve with AI',
}: AiImproveDialogProps) {
  const {
    settings,
    isConfigured,
    setApiKey,
    setDefaultModel,
    setPromptTemplate,
    resetPromptTemplates,
  } = useAiSettings();

  const [selectedModel, setSelectedModel] = useState(settings.defaultModel);
  const [showSettings, setShowSettings] = useState(false);
  const [instruction, setInstruction] = useState(initialInstruction ?? '');
  const [followUp, setFollowUp] = useState('');
  const [models, setModels] = useState<OpenRouterModel[]>([]);
  const [showOriginal, setShowOriginal] = useState(false);

  const selectedModelInfo = useMemo(
    () => models.find((m) => m.id === selectedModel) ?? null,
    [models, selectedModel],
  );

  const {
    state,
    isImproving,
    tokenUsage,
    improve,
    sendFollowUp,
    cancel,
    reset,
  } = useAiImprove();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const instructionRef = useRef<HTMLTextAreaElement>(null);
  const followUpRef = useRef<HTMLTextAreaElement>(null);

  // Sync default model from settings
  useEffect(() => {
    if (settings.defaultModel && !selectedModel) {
      setSelectedModel(settings.defaultModel);
    }
  }, [settings.defaultModel, selectedModel]);

  // Load models when dialog opens
  useEffect(() => {
    if (open && isConfigured) {
      const loadModels = async () => {
        try {
          const client = createAiClient(settings.apiKey);
          const fetched = await fetchModels(client);
          setModels(fetched);
        } catch {
          // Non-critical
        }
      };
      loadModels();
    }
  }, [open, isConfigured, settings.apiKey]);

  // Reset when the dialog closes
  useEffect(() => {
    if (!open) {
      reset();
      setInstruction(initialInstruction ?? '');
      setFollowUp('');
      setShowOriginal(false);
    }
  }, [open, reset, initialInstruction]);

  // Scroll to the bottom whenever the message list or streaming content changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, state.currentContent]);

  // Focus the instruction textarea when dialog opens
  useEffect(() => {
    if (open && !state.messages.length) {
      setTimeout(() => instructionRef.current?.focus(), 50);
    }
  }, [open, state.messages.length]);

  const handleImprove = useCallback(async () => {
    const inst = instruction.trim();
    if (!inst || isImproving || !isConfigured) return;
    await improve(
      settings.apiKey,
      selectedCode,
      contextCode,
      inst,
      selectedModel,
      selectedModelInfo,
      systemPromptOverride,
    );
  }, [
    instruction,
    isImproving,
    isConfigured,
    improve,
    settings.apiKey,
    selectedCode,
    contextCode,
    selectedModel,
    selectedModelInfo,
  ]);

  const handleInstructionKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleImprove();
      }
    },
    [handleImprove],
  );

  const handleSendFollowUp = useCallback(async () => {
    const q = followUp.trim();
    if (!q || isImproving) return;
    setFollowUp('');
    await sendFollowUp(settings.apiKey, q, selectedModel, selectedModelInfo);
  }, [
    followUp,
    isImproving,
    sendFollowUp,
    settings.apiKey,
    selectedModel,
    selectedModelInfo,
  ]);

  const handleFollowUpKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSendFollowUp();
      }
    },
    [handleSendFollowUp],
  );

  // Extract the latest improved code block from the last assistant message
  const latestImprovedCode = useMemo(() => {
    // Walk backwards through messages to find the last assistant response
    for (let i = state.messages.length - 1; i >= 0; i--) {
      const msg = state.messages[i];
      if (msg.role === 'assistant') {
        const code = extractImprovedCode(msg.content);
        if (code) return code;
      }
    }
    // Also try the live streaming content
    if (state.currentContent) {
      return extractImprovedCode(state.currentContent);
    }
    return null;
  }, [state.messages, state.currentContent]);

  const handleAccept = useCallback(() => {
    if (latestImprovedCode) {
      onApply(latestImprovedCode);
      onOpenChange(false);
    }
  }, [latestImprovedCode, onApply, onOpenChange]);

  const hasStarted = state.messages.length > 0 || isImproving;
  const canSendFollowUp =
    !isImproving && state.step === 'complete' && followUp.trim().length > 0;
  const canAccept =
    !isImproving && state.step === 'complete' && latestImprovedCode !== null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="!flex h-[85vh] max-h-[85vh] flex-col !gap-0 sm:max-w-[700px]">
          {/* Header */}
          <DialogHeader className="flex-shrink-0 pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 className="h-5 w-5 text-primary" />
                <DialogTitle>{title}</DialogTitle>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowSettings(true)}
                    className="h-8 w-8"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="sr-only">AI Settings</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>AI Settings</TooltipContent>
              </Tooltip>
            </div>
          </DialogHeader>

          {/* --- Pre-flight: model selector + instruction input --- */}
          {!hasStarted && (
            <div className="flex-shrink-0 space-y-3 pb-3">
              {!isConfigured && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Please configure your OpenRouter API key in{' '}
                    <button
                      onClick={() => setShowSettings(true)}
                      className="font-medium underline underline-offset-2"
                    >
                      settings
                    </button>{' '}
                    to use AI features.
                  </AlertDescription>
                </Alert>
              )}
              <ModelSelector
                apiKey={settings.apiKey}
                value={selectedModel}
                onValueChange={(value) => {
                  setSelectedModel(value);
                  setDefaultModel(value);
                }}
                disabled={!isConfigured}
              />

              {/* Selected code preview */}
              {selectedCode && (
                <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
                  <p className="mb-1 text-xs font-medium text-muted-foreground">
                    Selected code
                  </p>
                  <pre className="max-h-[120px] overflow-auto whitespace-pre-wrap font-mono text-xs">
                    {selectedCode}
                  </pre>
                </div>
              )}

              {/* Instruction input */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">
                  What would you like to improve?
                </p>
                <div className="flex gap-2">
                  <Textarea
                    ref={instructionRef}
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    onKeyDown={handleInstructionKeyDown}
                    placeholder='e.g. "Add comments", "Simplify the logic", "Make it more readable"… (Cmd/Ctrl+Enter to send)'
                    rows={3}
                    disabled={!isConfigured}
                    className="resize-none"
                  />
                  <Button
                    className="h-auto self-stretch px-4"
                    onClick={handleImprove}
                    disabled={
                      !isConfigured || !instruction.trim() || isImproving
                    }
                    title="Improve (Cmd+Enter)"
                  >
                    <Wand2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* --- Chat thread (shown once the request has started) --- */}
          {hasStarted && (
            <ScrollArea className="min-h-0 flex-1 rounded-md border border-border bg-background">
              <div className="flex flex-col gap-4 p-4">
                {state.messages.map((msg, i) => (
                  <div
                    key={`${msg.role}-${i}`}
                    className={cn(
                      'flex gap-3',
                      msg.role === 'user' ? 'justify-end' : 'justify-start',
                    )}
                  >
                    {msg.role === 'assistant' && (
                      <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Bot className="h-4 w-4" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'max-w-[88%] rounded-lg px-3 py-2 text-sm',
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted',
                      )}
                    >
                      {msg.role === 'user' ? (
                        <p className="whitespace-pre-wrap">
                          {i === 0 ? state.userInstruction : msg.content}
                        </p>
                      ) : (
                        <div className="prose prose-sm max-w-none dark:prose-invert prose-code:before:content-none prose-code:after:content-none prose-pre:bg-background/60 [&>:first-child]:mt-0 [&>:last-child]:mb-0">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                    {msg.role === 'user' && (
                      <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <User className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                ))}

                {/* Streaming assistant response */}
                {isImproving && state.currentContent && (
                  <div className="flex justify-start gap-3">
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Bot className="h-4 w-4" />
                    </div>
                    <div className="max-w-[88%] rounded-lg bg-muted px-3 py-2 text-sm">
                      <div className="prose prose-sm max-w-none dark:prose-invert prose-code:before:content-none prose-code:after:content-none prose-pre:bg-background/60 [&>:first-child]:mt-0 [&>:last-child]:mb-0">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {state.currentContent}
                        </ReactMarkdown>
                      </div>
                      <span className="ml-0.5 inline-block h-3.5 w-0.5 animate-pulse bg-foreground/60 align-text-bottom" />
                    </div>
                  </div>
                )}

                {/* Placeholder while waiting for first chunk */}
                {isImproving && !state.currentContent && (
                  <div className="flex justify-start gap-3">
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Bot className="h-4 w-4" />
                    </div>
                    <div className="rounded-lg bg-muted px-3 py-2">
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:0ms]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:150ms]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:300ms]" />
                      </span>
                    </div>
                  </div>
                )}

                {/* Error */}
                {state.step === 'error' && state.error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{state.error}</AlertDescription>
                  </Alert>
                )}

                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          )}

          {/* --- Diff panel: shown once complete and improved code is available --- */}
          {canAccept && latestImprovedCode && (
            <div className="flex-shrink-0 space-y-2 pt-3">
              {/* Collapsible original code */}
              <div className="rounded-md border border-border">
                <button
                  className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                  onClick={() => setShowOriginal((v) => !v)}
                >
                  <span>Original code</span>
                  {showOriginal ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </button>
                {showOriginal && (
                  <pre className="max-h-[140px] overflow-auto whitespace-pre-wrap border-t border-border bg-muted/40 px-3 py-2 font-mono text-xs">
                    {state.selectedCode}
                  </pre>
                )}
              </div>

              {/* Improved code preview */}
              <div className="rounded-md border border-primary/40 bg-primary/5">
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-xs font-medium text-primary">
                    Improved code
                  </span>
                </div>
                <pre className="max-h-[160px] overflow-auto whitespace-pre-wrap border-t border-primary/20 px-3 py-2 font-mono text-xs">
                  {latestImprovedCode}
                </pre>
              </div>

              {/* Accept / Discard buttons */}
              <div className="flex gap-2">
                <Button className="flex-1 gap-2" onClick={handleAccept}>
                  <Check className="h-4 w-4" />
                  Accept
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 gap-2"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="h-4 w-4" />
                  Discard
                </Button>
              </div>
            </div>
          )}

          {/* Token usage */}
          {tokenUsage.totalTokens > 0 && (
            <div className="flex-shrink-0 pt-2">
              <AiTokenDisplay tokenUsage={tokenUsage} />
            </div>
          )}

          {/* Follow-up input — shown once conversation has started */}
          {hasStarted && (
            <div className="flex-shrink-0 pt-3">
              <div className="flex gap-2">
                <Textarea
                  ref={followUpRef}
                  value={followUp}
                  onChange={(e) => setFollowUp(e.target.value)}
                  onKeyDown={handleFollowUpKeyDown}
                  placeholder="Ask a follow-up… e.g. 'Also add comments' (Cmd/Ctrl+Enter to send)"
                  rows={2}
                  disabled={isImproving}
                  className="resize-none"
                />
                {isImproving ? (
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-auto self-stretch"
                    onClick={cancel}
                    title="Cancel"
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    size="icon"
                    className="h-auto self-stretch"
                    onClick={handleSendFollowUp}
                    disabled={!canSendFollowUp}
                    title="Send (Cmd+Enter)"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Footer close button — only when conversation hasn't started */}
          {!hasStarted && (
            <div className="mt-2 flex flex-shrink-0 justify-end border-t pt-3">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AiSettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        apiKey={settings.apiKey}
        onApiKeyChange={setApiKey}
        promptTemplates={settings.promptTemplates}
        onPromptTemplateChange={setPromptTemplate}
        onResetPromptTemplates={resetPromptTemplates}
      />
    </>
  );
}
